import React, { useState, useEffect, useMemo, useRef } from 'react';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUser, faIdCard, faShieldHalved, faBullseye, faCheck, faPen,
    faArrowTrendUp, faBoltLightning, faFlask, faCalendarDays, faWaveSquare, faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons";

import { Checkbox } from '@/components/form/Choice.jsx';
import { PrimaryButton, SecondaryButton } from "../../../../components/button/Button.jsx";
import { UserAuth } from '../../../../authentication/AuthContext.jsx';
import { getOrgById } from '../../../../services/admin-settings/org-management/OrgRegistrationService.jsx';
import { getGameRegistries } from '../../../../services/admin-settings/GameService.jsx';
import { invitationGeographies, geographiesText } from './invitationAccess.js';
import useRoleNames from '../../../../hooks/useRoleNames.js';

import './AccessManagementInvitationAccept.css';

// Mirrors the onboarding Goal step so an invited user gets the same guided
// "pick your primary goal" experience without going through the full create flow.
const GOALS = [
    { id: 'grow',     title: 'Grow & retain players',      blurb: 'Cohorts, retention curves, churn signals.', tint: 'green',  icon: faArrowTrendUp },
    { id: 'monetize', title: 'Monetize better',            blurb: 'Pricing, offers, real-time ARPDAU lift.',   tint: 'amber',  icon: faBoltLightning },
    { id: 'test',     title: 'Test changes safely',        blurb: 'A/B tests with guardrails & CIs.',          tint: 'red',    icon: faFlask },
    { id: 'liveops',  title: 'Orchestrate live events',    blurb: 'Plan, launch, and measure events.',         tint: 'pink',   icon: faCalendarDays },
    { id: 'behavior', title: 'Understand player behavior', blurb: 'Funnels, paths, session intelligence.',     tint: 'blue',   icon: faWaveSquare },
    { id: 'self',     title: "I'll explore later",         blurb: 'Skip for now, discover modules as you go.', tint: 'purple', icon: faWandMagicSparkles },
];

const AccessManagementInvitationAccept = ({ onAccept, onDecline, invitation, existingUser, acceptLoading, declineLoading }) => {
    const { user } = UserAuth();
    const isExistingUser = !!existingUser;

    // New users get a guided 2-step flow (details → goal); existing users just
    // confirm (they already have a name + goal on record).
    const [step, setStep] = useState(1);
    const [acknowledged, setAcknowledged] = useState(false);
    const [name, setName] = useState('');
    const [editingName, setEditingName] = useState(false);
    const [goal, setGoal] = useState('');
    const [orgName, setOrgName] = useState('');
    const [gameRegistries, setGameRegistries] = useState([]);
    const { roleLabel } = useRoleNames();
    const bodyRef = useRef(null);

    const email = invitation?.targetUserEmail || '';
    const gameDetailsMap = invitation?.orgDetails?.gameDetailsMap;
    const hasGameDetails = gameDetailsMap && Object.keys(gameDetailsMap).length > 0;

    // Prefill the name from the Google sign-in display name so a brand-new user
    // doesn't have to retype it (they can still edit). Persisted on accept.
    useEffect(() => {
        if (!isExistingUser && user?.displayName) setName(user.displayName);
    }, [isExistingUser, user?.displayName]);

    // The invitation already CARRIES the org name — the sender resolves it from the registry and
    // stores it on orgDetails precisely so this screen doesn't have to ask. Reading it back over
    // /org-registry/{id} was the fragile path: the invitee is not a member of that org yet (that is
    // the whole point of the invitation), so the read is not theirs to make and the field rendered
    // blank. The fetch stays only as a fallback for older rows stored without a name.
    useEffect(() => {
        const carried = invitation?.orgDetails?.orgName;
        if (carried) {
            setOrgName(carried);
            return;
        }
        const fetchOrg = async () => {
            const orgId = invitation?.orgDetails?.orgId;
            if (!orgId || !user?.accessToken) return;
            try {
                const orgData = await getOrgById({ accessToken: user.accessToken, id: orgId });
                setOrgName(orgData?.org?.orgName || '');
            } catch (err) {
                console.error('Failed to fetch org:', err);
            }
        };
        fetchOrg();
    }, [invitation?.orgDetails?.orgId, invitation?.orgDetails?.orgName, user?.accessToken]);

    useEffect(() => {
        if (!hasGameDetails || !user?.accessToken) return;
        const fetchGameData = async () => {
            try {
                const gamesRes = await getGameRegistries({ accessToken: user.accessToken });
                setGameRegistries(gamesRes?.games || []);
            } catch (err) {
                console.error('Failed to fetch game data:', err);
            }
        };
        fetchGameData();
    }, [hasGameDetails, user?.accessToken]);



    const gameName = useMemo(() => {
        if (!hasGameDetails) return null;
        return Object.entries(gameDetailsMap).map(([id, gd]) => {
            if (gd?.gameName) return gd.gameName;
            const byId = gameRegistries.find(g => g.id === id);
            if (byId) return byId.gameName;
            const byClientId = gameRegistries.find(g =>
                g.builds?.some(group => group.gameClientId === id)
            );
            return byClientId?.gameName || id;
        }).join(', ');
    }, [hasGameDetails, gameDetailsMap, gameRegistries]);

    // Where the access applies on each game: the geographies it is limited to, or the whole game.
    const gameGeographies = useMemo(
        () => (hasGameDetails
            ? invitationGeographies(gameDetailsMap, (id) => gameRegistries.find(g => g.id === id)?.gameName || id)
            : []),
        [hasGameDetails, gameDetailsMap, gameRegistries],
    );

    // The invitation's top-level role (for the read-only "Role" display).
    const invitedRoleLabel = useMemo(() => {
        const gd = hasGameDetails ? Object.values(gameDetailsMap)?.[0] : null;
        // Server-resolved name first (custom roles); else the catalogue/enum via roleLabel. An org-scope
        // invite (e.g. CEO) has no games: its role is the organization role, not the level ("Member").
        const org = invitation?.orgDetails;
        return gd?.roleName || (gd?.role && roleLabel(gd.role))
            || org?.roleName || (org?.role && roleLabel(org.role))
            || roleLabel(invitation?.invitationLevel);
    }, [hasGameDetails, gameDetailsMap, invitation?.orgDetails, invitation?.invitationLevel, roleLabel]);

    const onGoalStep = !isExistingUser && step === 2;
    const nameMissing = !isExistingUser && name.trim() === '';
    const detailsReady = isExistingUser ? acknowledged : (acknowledged && !nameMissing);

    // The body node stays mounted across the step switch, so it kept whatever
    // offset it already had — step 1 reopened (e.g. via Back) scrolled past the
    // required Name field sitting at the top.
    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [step]);

    const handlePrimary = () => {
        if (isExistingUser) { onAccept({}); return; }
        if (step === 1) { setStep(2); return; }
        onAccept({ name: name.trim(), goal });
    };

    return (
        <div className="inv-accept-card">
            {/* ===== Header ===== */}
            <div className="inv-accept-header">
                <div className="inv-accept-header-icon-wrapper">
                    <FontAwesomeIcon icon={onGoalStep ? faBullseye : faUser} />
                </div>
                <div className="inv-accept-header-title-section">
                    <h2 className="inv-accept-header-title">
                        {isExistingUser ? 'Accept Invitation'
                            : onGoalStep ? 'Pick your primary goal'
                            : 'Welcome — set up your access'}
                    </h2>
                    <p className="inv-accept-header-subtitle">
                        {isExistingUser ? 'Accept the invite to join and access assigned tools and projects.'
                            : onGoalStep ? "We'll tailor your Home dashboard to the right modules."
                            : 'Review what you’re being granted, confirm your name, then pick a goal.'}
                    </p>
                </div>
            </div>

            {/* ===== Scrollable Body ===== */}
            <div className="inv-accept-body" ref={bodyRef}>
                {onGoalStep ? (
                    /* ----- Step 2 (new users): primary goal ----- */
                    <div className="inv-accept-section">
                        <div className="inv-accept-goal-grid">
                            {GOALS.map((g) => {
                                const sel = goal === g.id;
                                return (
                                    <button
                                        key={g.id}
                                        type="button"
                                        className={`inv-accept-goal-card ${sel ? 'selected' : ''}`}
                                        onClick={() => setGoal(sel ? '' : g.id)}
                                    >
                                        <span className={`inv-accept-goal-icon inv-accept-goal-icon--${g.tint}`}>
                                            <FontAwesomeIcon icon={g.icon} />
                                        </span>
                                        <span className="inv-accept-goal-text">
                                            <span className="inv-accept-goal-title">{g.title}</span>
                                            <span className="inv-accept-goal-blurb">{g.blurb}</span>
                                        </span>
                                        {sel && <span className="inv-accept-goal-check"><FontAwesomeIcon icon={faCheck} /></span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Section: User Profile */}
                        <div className="inv-accept-section">
                            <h3 className="inv-accept-section-title">
                                <FontAwesomeIcon icon={faIdCard} />
                                User Profile
                            </h3>
                            {isExistingUser ? (
                                <div className="inv-accept-fields-grid">
                                    <div className="inv-accept-field">
                                        <span className="inv-accept-field-label">Name</span>
                                        <span className="inv-accept-field-value">{existingUser.userName || '-'}</span>
                                    </div>
                                    <div className="inv-accept-field">
                                        <span className="inv-accept-field-label">Role</span>
                                        <span className="inv-accept-field-value">{invitedRoleLabel}</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="inv-accept-fields-grid">
                                    <div className="inv-accept-field">
                                        <span className="inv-accept-field-label">
                                            Name<span className="inv-accept-required">*</span>
                                        </span>
                                        {(editingName || nameMissing) ? (
                                            <>
                                                <input
                                                    className={`inv-accept-name-input${nameMissing ? ' error' : ''}`}
                                                    value={name}
                                                    autoFocus={editingName || nameMissing}
                                                    placeholder="Enter your name"
                                                    onChange={(e) => { setName(e.target.value); setEditingName(true); }}
                                                    onBlur={() => setEditingName(false)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') setEditingName(false); }}
                                                />
                                                {nameMissing && (
                                                    <span className="inv-accept-field-hint">Enter your name to continue</span>
                                                )}
                                            </>
                                        ) : (
                                            <span className="inv-accept-field-value inv-accept-field-value--editable">
                                                {name}
                                                <button
                                                    type="button"
                                                    className="inv-accept-edit-btn"
                                                    aria-label="Edit name"
                                                    onClick={() => setEditingName(true)}
                                                >
                                                    <FontAwesomeIcon icon={faPen} />
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                    <div className="inv-accept-field">
                                        <span className="inv-accept-field-label">Role</span>
                                        <span className="inv-accept-field-value">{invitedRoleLabel}</span>
                                    </div>
                                </div>
                            )}
                            <div className="inv-accept-fields-grid">
                                <div className="inv-accept-field">
                                    <span className="inv-accept-field-label">Email</span>
                                    <span className="inv-accept-field-value">{email}</span>
                                </div>
                                <div className="inv-accept-field">
                                    <span className="inv-accept-field-label">Organization Name</span>
                                    <span className="inv-accept-field-value">{orgName}</span>
                                </div>
                            </div>
                        </div>

                        {/* Section: Game Details (only when game details exist) */}
                        {hasGameDetails && (
                            <>
                                <hr />
                                <div className="inv-accept-section">
                                    <h3 className="inv-accept-section-title">
                                        <FontAwesomeIcon icon={faIdCard} />
                                        Game Details
                                    </h3>
                                    <div className="inv-accept-fields-grid">
                                        <div className="inv-accept-field">
                                            <span className="inv-accept-field-label">Game</span>
                                            <span className="inv-accept-field-value">{gameName}</span>
                                        </div>
                                        {/* Beside Game: where on it the access applies (wraps when long). */}
                                        <div className="inv-accept-field">
                                            <span className="inv-accept-field-label">Geographies</span>
                                            <span className="inv-accept-field-value inv-accept-field-value--wrap">
                                                {geographiesText(gameGeographies)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}


                    </>
                )}
            </div>

            {/* Compliance acknowledgement — pinned just above the footer (outside
                the scroll) so this gating control is always visible next to the
                primary action, instead of hiding below the fold. */}
            {!onGoalStep && (
                <div className="inv-accept-ack">
                    <FontAwesomeIcon icon={faShieldHalved} className="inv-accept-ack-icon" />
                    <Checkbox
                        label="I acknowledge and accept the workspace policies"
                        name="t&c"
                        value="t&c"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                    />
                </div>
            )}

            {/* ===== Footer ===== */}
            <div className="inv-accept-footer">
                {onGoalStep ? (
                    <SecondaryButton onClick={() => setStep(1)}>
                        Back
                    </SecondaryButton>
                ) : (
                    <SecondaryButton onClick={onDecline} disabled={declineLoading || acceptLoading}>
                        {declineLoading ? 'Declining...' : 'Decline'}
                    </SecondaryButton>
                )}
                <PrimaryButton
                    onClick={handlePrimary}
                    disabled={(onGoalStep ? !goal : !detailsReady) || acceptLoading || declineLoading}
                >
                    {acceptLoading
                        ? (isExistingUser ? 'Accepting...' : 'Joining...')
                        : (isExistingUser ? 'Accept' : onGoalStep ? 'Accept & Join' : 'Next')}
                </PrimaryButton>
            </div>
        </div>
    );
};

export default AccessManagementInvitationAccept;
